/*!
 * Music Forecast — ソフトナビゲーション
 * ページ遷移でドキュメントを作り直さず、main の中身だけ差し替える。
 * これにより body 直下の #mf-yt（YouTube iframe）が破棄されず、再生が途切れない。
 *
 * 置き場所 : テーマ → コードを編集 → Assets → mf-softnav.js
 * 読み込み : layout/theme.liquid の </body> 直前に
 *            <script src="{{ 'mf-softnav.js' | asset_url }}" defer></script>
 *
 * 無効化   : URL に ?nosoftnav=1 を付ける / localStorage.mfSoftNavOff = '1'
 *
 * 前提（2026-09-17 に本番で確認済み）
 *   - 差し替え領域は main#MainContent、プレイヤーは body 直下の #mf-yt と #mf-player
 *   - Shopify.theme.sections.registered[type] がセクションのコンストラクタ
 *   - Alpine 2.8.2（discoverUninitializedComponents / initializeComponent あり）
 *   - main 内の script は application/json と ld+json のみ（実行コード無し）
 *
 * 2026-09-19 修正: コレクション2ページ目以降 → 商品 → 戻る で1ページ目に戻ってしまう不具合
 *   原因: テーマの filterable セクション（Alpine）が mounted() で window に popstate リスナーを登録し、
 *         e.state.searchParams（テーマ自身のフィルタ状態）を読んで AJAX で再描画する。
 *         ソフトナビの state には searchParams が無いため "" と解釈され、1ページ目が描画されていた。
 *         さらにソフトナビで差し替えるたびに新しい filterable が mount され、リスナーが増え続けていた。
 *   対策: (1) 初回読み込み時に mount 済みの filterable の renderPage を包み、ソフトナビの履歴では何もしないようにする
 *            （テーマのリスナーはソフトナビより先に登録されているため、イベント側からは止められない）
 *         (2) 差し替え中に登録される popstate リスナーを捨てる（増殖防止）
 *         (3) 保険として state に searchParams（URL のクエリ）を持たせ、万一テーマ側が描画しても正しいページになるようにする
 *
 * 2026-09-21 / information-navigation v1.1 (staging)
 * Shipping policy joins the existing soft-navigation allowlist. Grading and
 * shipping links are handled in capture phase, without replacing the player.
 * Preserve preview theme, fragment destinations and fragment-only history.
 * For these help routes, a failed fetch offers retry instead of silently
 * unloading the document (and stopping the preview).
 *
 * 2026-09-22 / information-navigation v1.2
 * All Shopify policy pages (JP/EN) now use the same protected soft-navigation
 * path so the persistent player and YouTube iframe survive policy navigation.
 *
 * 2026-09-22 / never-lose-your-place v1.0
 * Browse → product → Back now restores not only the URL/filter/page and scrollY,
 * but the exact product card's viewport position. This survives card-height
 * changes, filters/sort/page URLs, Quick Dig → VIEW RECORD, and hard-load
 * product fallbacks whenever the source history entry is available.
 *
 * 2026-10-20 / player-link v1.0
 * プレイヤーバーのジャケットと商品名から商品ページへ移っても再生を止めない。
 * 以前は #mf-player 内のリンクをすべて対象外にしていたため、通常遷移で iframe が作り直されていた。
 * いま開いている商品を指す場合は再読み込みせず、ページ先頭へ戻るだけにする。
 */
(function () {
  'use strict';

  if (window.__mfSoftNav) return;
  if (!window.history || !window.fetch || !window.DOMParser) return;
  try { if (localStorage.getItem('mfSoftNavOff') === '1') return; } catch (e) {}
  if (/[?&]nosoftnav=1/.test(location.search)) return;
  if (window.Shopify && window.Shopify.designMode) return;   // テーマエディタでは動かさない
  window.__mfSoftNav = true;

  var MAIN = 'main#MainContent';
  function log() { try { console.log.apply(console, ['[softnav]'].concat([].slice.call(arguments))); } catch (e) {} }
  var CACHE = {};          // URL → HTML（hover 先読み）
  var CACHE_MAX = 12;
  var busy = null;         // 実行中の AbortController
  var navigationId = 0;
  var renderedUrl = location.pathname + location.search + location.hash;
  var previewTheme = '';
  try { previewTheme = new URL(location.href).searchParams.get('preview_theme_id') || ''; } catch (e) {}

  function routeUrl(value) {
    var u = new URL(value, location.href);
    if (previewTheme && u.origin === location.origin && !u.searchParams.has('preview_theme_id')) {
      u.searchParams.set('preview_theme_id', previewTheme);
    }
    return u.pathname + u.search + u.hash;
  }

  var stockEntry = routeUrl(location.href);
  if (stockEntry !== location.pathname + location.search + location.hash) {
    location.replace(stockEntry);
    return;
  }

  function productHandle(value) {
    var path = '';
    try { path = new URL(value, location.href).pathname; } catch (e) { return ''; }
    var m = path.match(/\/products\/([^/?#]+)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  function findProductCard(handle) {
    if (!handle) return null;
    var cards = document.querySelectorAll('[data-product-grid-item]');
    for (var i = 0; i < cards.length; i++) {
      var links = cards[i].querySelectorAll('a[href*="/products/"]');
      for (var j = 0; j < links.length; j++) {
        if (productHandle(links[j].href) === handle) return cards[i];
      }
    }
    return null;
  }

  /* 保存時のカード位置は document 上の座標ではなく viewport 上の top。
     戻ったページでカードの高さや上部コンテンツが少し変わっても、
     同じ盤を同じ画面位置へ戻せる。Quick Dig の VIEW RECORD は drawer 内に
     複製されたリンクなので、リンク先 handle から元カードを探し直す。 */
  function placeForLink(a) {
    if (!a || !a.href) return null;
    var handle = productHandle(a.href);
    if (!handle) return null;
    var card = a.closest && a.closest('[data-product-grid-item]');
    if (!card) card = findProductCard(handle);
    if (!card) return { handle: handle };
    var rect = card.getBoundingClientRect();
    return { handle: handle, top: Math.round(rect.top) };
  }

  function restorePlace(place, fallbackY) {
    window.scrollTo(0, fallbackY || 0);
    if (!place || !place.handle) return;

    var tries = 0;
    function settle() {
      var card = findProductCard(place.handle);
      if (!card) {
        if (++tries < 8) setTimeout(settle, 70);
        return;
      }
      var desired = (typeof place.top === 'number') ? place.top : 80;
      var delta = card.getBoundingClientRect().top - desired;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);

      /* Card enhancer / lazy image initialization can move rows during the first
         few frames. Re-anchor briefly, then stop touching the user's scroll. */
      tries++;
      if (tries < 5) setTimeout(settle, 80);
      else {
        try { document.dispatchEvent(new CustomEvent('mf:place-restored', { detail: { handle: place.handle } })); } catch (e) {}
      }
    }
    (window.requestAnimationFrame || setTimeout)(function () {
      (window.requestAnimationFrame || setTimeout)(settle);
    });
  }

  function scrollDestination(url, restoreY, place) {
    var hash = new URL(url, location.href).hash;
    if (hash) {
      var id;
      try { id = decodeURIComponent(hash.slice(1)); } catch (e) { id = hash.slice(1); }
      var target = document.getElementById(id);
      if (target) { target.scrollIntoView({ block: 'start' }); return; }
    }
    if (place && place.handle) restorePlace(place, restoreY);
    else window.scrollTo(0, restoreY || 0);
  }

  /* ソフトナビが history に書く state。
     searchParams はテーマの filterable が popstate で読む項目（フィルタ・並べ替え・page）。
     万一テーマのリスナーが先に動いても、正しいページを描画させるために URL のクエリをそのまま渡す */
  function stateFor(y, search, place) {
    var q = (search == null ? location.search : search) || '';
    if (q.charAt(0) === '?') q = q.slice(1);
    var state = { mf: 1, y: y || 0, searchParams: q };
    if (place && place.handle) state.place = place;
    return state;
  }

  /* 初回読み込みで mount 済みの filterable（テーマ）の popstate 処理を無効にする。
     テーマは mounted() で window に popstate リスナーを登録し、onHistoryChange → this.renderPage(params, null, false) を呼ぶ。
     このリスナーはソフトナビより先に登録されているため、イベントの伝播では止められない。
     代わりに、Alpine コンポーネントの renderPage を包み、「URL 更新なし（第3引数 false）かつソフトナビの履歴」のときは何もしない。
     差し替え後に mount される filterable はリスナー登録自体を捨てる（swap 内） */
  function muteThemeHistory() {
    var els = document.querySelectorAll('[data-section-type="filterable"]');
    for (var i = 0; i < els.length; i++) {
      var c = els[i].__x;
      var d = c && (c.unobservedData || c.$data);
      if (!d || typeof d.renderPage !== 'function' || d.__mfMuted) continue;
      (function (data) {
        data.renderPage = function (params, ev, updateUrl) {
          if (updateUrl === false && history.state && history.state.mf) {
            if (window.mfSoftNavDebug) log('mute theme renderPage on popstate');
            return;
          }
          var target = new URL(location.href);
          var uniqueParams = new URLSearchParams();
          new URLSearchParams(params || '').forEach(function (value, name) {
            if (uniqueParams.getAll(name).indexOf(value) === -1) uniqueParams.append(name, value);
          });
          target.search = uniqueParams.toString();
          var y = window.scrollY || 0;
          var focusId = ev && ev.target && ev.target.id;
          if (updateUrl !== false) saveScroll();
          go(target.pathname + target.search, updateUrl !== false, y, null, {
            preserveScroll: true,
            focusId: focusId,
            announceResults: true
          });
        };
        data.__mfMuted = true;
      })(d);
    }
  }

  /* ---------------------------------------------------------------- 対象の判定 */

  // ここ以外は通常のページ遷移に任せる
  var DENY = /\/(cart|checkout|account|challenge|orders|apps|tools|services|localization|password)(\/|$|\?)|\/\.well-known|\/admin/;
  var ALLOW = /^\/(en\/)?(collections|products|pages|blogs|search)(\/|$|\?)|^\/(en)?\/?$/;

  var INFO = /^\/(?:en\/)?(?:policies\/[^/?#]+|pages\/grading)\/?$/;

  function sameOrigin(a) {
    return a.protocol === location.protocol && a.host === location.host;
  }

  function navigable(a, e) {
    if (!a || !a.href) return false;
    if (e && (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return false;
    if (a.hasAttribute('download') || a.target && a.target !== '_self') return false;
    if (!sameOrigin(a)) return false;
    if (a.getAttribute('href').charAt(0) === '#') return false;
    if (a.pathname === location.pathname && a.search === location.search) return false;
    if (DENY.test(a.pathname)) return false;
    if (!ALLOW.test(a.pathname) && !INFO.test(a.pathname)) return false;
    // プレイヤーの操作、フォーム内のリンク（フィルタ・並べ替えはテーマが自前で AJAX する）は触らない。
    // ただしプレイヤー内でも、ジャケットと商品名（商品ページへのリンク）はソフトナビで開く（2026-10-20）
    if (a.closest('#mf-player')) {
      if (!(a.classList.contains('mf-thumb-link') || a.closest('.mf-info__product'))) return false;
    } else if (a.closest('.mf-listen, .mf-play, .mf-playall, form')) {
      return false;
    }
    if (a.hasAttribute('data-no-softnav')) return false;
    return true;
  }

  /* ---------------------------------------------------------------- 進行表示 */

  var bar;
  function barStart() {
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'mf-softnav-bar';
      bar.style.cssText = 'position:fixed;top:0;left:0;height:2px;width:0;background:#161819;' +
        'z-index:99999;transition:width .25s ease,opacity .3s;pointer-events:none;opacity:0';
      document.body.appendChild(bar);
    }
    bar.style.transition = 'none';  bar.style.width = '0';  bar.style.opacity = '1';
    void bar.offsetWidth;
    bar.style.transition = 'width 1.2s cubic-bezier(.2,.8,.2,1)';
    bar.style.width = '75%';
  }
  function barDone() {
    if (!bar) return;
    bar.style.transition = 'width .2s ease, opacity .35s .15s';
    bar.style.width = '100%';  bar.style.opacity = '0';
  }

  /* ---------------------------------------------------------------- 取得 */

  function fetchPage(url) {
    url = routeUrl(url).split('#')[0];
    if (CACHE[url]) return Promise.resolve(CACHE[url]);
    if (busy) { try { busy.abort(); } catch (e) {} }
    busy = ('AbortController' in window) ? new AbortController() : null;
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'mf-softnav' },
      signal: busy ? busy.signal : undefined
    }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return r.text();
    }).then(function (html) {
      var keys = Object.keys(CACHE);
      if (keys.length >= CACHE_MAX) delete CACHE[keys[0]];
      CACHE[url] = html;
      return html;
    });
  }

  function prefetch(url) {
    url = routeUrl(url).split('#')[0];
    if (CACHE[url]) return;
    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'mf-softnav' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) { if (t) CACHE[url] = t; })
      .catch(function () {});
  }

  /* ReStock/Want is injected by an app embed on a full document load.
     For sold-out product pages, soft-swapping MainContent skips that app's
     page-load initialization. Detect those targets from the fetched HTML
     and fall back to a normal navigation only for sold-out products.
     In-stock product pages keep soft navigation and continuous playback. */
  function needsFullProductLoad(html, url) {
    var target;
    try { target = new URL(url, location.href); } catch (e) { return false; }
    if (!/^\/(?:en\/)?products\//.test(target.pathname)) return false;
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return false; }
    var btn = doc.querySelector('form[data-product-form] .add-to-cart-btn');
    if (!btn) return false;
    var txt = (btn.textContent || '').replace(/\s+/g, ' ').trim();
    return btn.hasAttribute('disabled') || /sold\s*out|売り切れ|在庫切れ/i.test(txt);
  }

  /* ---------------------------------------------------------------- 差し替え */

  var S = function () { return (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) || null; };

  function unloadSections(root) {
    var s = S(); if (!s || !s.instances) return;
    var els = root.querySelectorAll('[data-section-type]');
    for (var i = 0; i < els.length; i++) {
      for (var j = s.instances.length - 1; j >= 0; j--) {
        if (s.instances[j].container === els[i]) {
          try { if (typeof s.instances[j].onUnload === 'function') s.instances[j].onUnload(); } catch (e) {}
          s.instances.splice(j, 1);
        }
      }
    }
  }

  function loadSections(root) {
    var s = S(); if (!s || !s.registered) return;
    var els = root.querySelectorAll('[data-section-type]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i], type = el.getAttribute('data-section-type');
      var C = s.registered[type];
      if (typeof C !== 'function') continue;
      var dup = false;
      for (var j = 0; j < s.instances.length; j++) if (s.instances[j].container === el) { dup = true; break; }
      if (dup) continue;
      // セクションの onLoad が window.ThemeSection_* を定義する。これが Alpine より先に要る
      try { s.instances.push(new C(el)); } catch (e) { if (window.mfSoftNavDebug) console.warn('[softnav] section', type, e); }
    }
  }

  function runInlineScripts(root) {
    // 通常 main 内は JSON だけだが、将来テーマが実行コードを入れた場合の保険
    var list = root.querySelectorAll('script');
    for (var i = 0; i < list.length; i++) {
      var s = list[i], t = (s.type || '').toLowerCase();
      if (t && t.indexOf('json') > -1) continue;
      if (s.src) continue;
      var n = document.createElement('script');
      n.textContent = s.textContent;
      s.parentNode.replaceChild(n, s);
    }
  }

  /* 差し替える1区画分の下準備。Alpine が挿入直後に動き出さないよう x-data を退避する。
     セクションの onLoad より先に評価されると ThemeSection_* 未定義で落ちるため */
  function hold(el) {
    var xs = el.querySelectorAll('[x-data]');
    for (var i = 0; i < xs.length; i++) {
      xs[i].setAttribute('data-mf-x', xs[i].getAttribute('x-data'));
      xs[i].removeAttribute('x-data');
    }
  }
  function release(el) {
    var xs = el.querySelectorAll('[data-mf-x]');
    for (var i = 0; i < xs.length; i++) {
      xs[i].setAttribute('x-data', xs[i].getAttribute('data-mf-x'));
      xs[i].removeAttribute('data-mf-x');
    }
  }
  function copyAttrs(cur, nu) {
    var keepId = cur.id;
    while (cur.attributes.length) cur.removeAttribute(cur.attributes[0].name);
    for (var i = 0; i < nu.attributes.length; i++) cur.setAttribute(nu.attributes[i].name, nu.attributes[i].value);
    if (!cur.id) cur.id = keepId;
  }

  /* body 直下で、ページによって中身が変わる区画を拾う。
     ヘッダーはトップページだけ大きなバナーを持つため、main だけ差し替えると
     トップから移動したときにバナーが残る（2026-09-17 に本番で発生）。
     アプリのブロック（.shopify-block）は第三者のスクリプトが二重に動く恐れがあるので触らない */
  function regions(doc) {
    var out = [];
    var nodes = doc.querySelectorAll('body > .shopify-section');
    for (var i = 0; i < nodes.length; i++) {
      var nu = nodes[i];
      if (!nu.id) continue;
      var cur = document.getElementById(nu.id);
      if (!cur || cur === nu) continue;
      if (cur.innerHTML === nu.innerHTML) continue;     // 同じなら触らない（通常はヘッダーもフッターも同じ）
      out.push({ cur: cur, nu: nu });
    }
    return out;
  }

  function swap(html, url) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var nm = doc.querySelector(MAIN), main = document.querySelector(MAIN);
    if (!nm || !main) return false;

    var localeChanged = document.documentElement.lang !== doc.documentElement.lang;
    syncThemeContext(doc);

    var parts = [{ cur: main, nu: nm }].concat(regions(doc));
    if (window.mfSoftNavDebug) log('swap', url, parts.map(function (p) { return p.cur.id || 'main'; }));

    // 1. 退避してから、まとめて中身を入れ替える
    var i;
    for (i = 0; i < parts.length; i++) {
      hold(parts[i].nu);
      unloadSections(parts[i].cur);
      copyAttrs(parts[i].cur, parts[i].nu);
      parts[i].cur.innerHTML = parts[i].nu.innerHTML;
    }

    // 2. body のクラスと ID（template-product / template-collection 等。配色とレイアウトが依存する）
    // Persistent player lives outside #MainContent, so preserve its transient body state
    // across soft navigation. Otherwise body.mf-player-open is lost and Shopify Inbox
    // can fall back over the fixed player after a page transition.
    var keepPlayerOpen = document.body.classList.contains('mf-player-open') ||
      !!document.querySelector('#mf-player.is-open');
    var nb = doc.body;
    if (nb) {
      document.body.className = nb.className;
      if (keepPlayerOpen) document.body.classList.add('mf-player-open');
      document.body.id = nb.id;
      for (var b = 0; b < nb.attributes.length; b++) {
        var at = nb.attributes[b];
        if (at.name !== 'class' && at.name !== 'id') document.body.setAttribute(at.name, at.value);
      }
    }

    // 3. html の言語属性と head の主要タグ
    var nh = doc.documentElement;
    if (nh) {
      var lang = nh.getAttribute('lang');
      var dir = nh.getAttribute('dir');
      if (lang) {
        document.documentElement.setAttribute('lang', lang);
        try { if (window.Shopify) window.Shopify.locale = lang; } catch (e) {}
      }
      if (dir) document.documentElement.setAttribute('dir', dir);
      else document.documentElement.removeAttribute('dir');
    }
    syncPageHead(doc);

    // 4. セクションを読み込む。ヘッダーの onLoad は MainContent の中を見るので、
    //    すべての中身を入れ替え終わってから走らせる必要がある
    //    この間に登録される popstate リスナー（filterable の mounted）は捨てる。
    //    戻る/進むはソフトナビが丸ごと差し替えるので不要で、残すと差し替えのたびに増えて多重 fetch になる
    var origAdd = window.addEventListener;
    window.addEventListener = function (type) {
      if (type === 'popstate') { if (window.mfSoftNavDebug) log('drop popstate listener'); return; }
      return origAdd.apply(window, arguments);
    };
    try {
      for (i = 0; i < parts.length; i++) { runInlineScripts(parts[i].cur); loadSections(parts[i].cur); }

      // 5. x-data を戻して Alpine に初期化させる
      for (i = 0; i < parts.length; i++) release(parts[i].cur);
      try {
        window.Alpine.discoverUninitializedComponents(function (c) { window.Alpine.initializeComponent(c); });
      } catch (e) { if (window.mfSoftNavDebug) console.warn('[softnav] alpine', e); }
    } finally {
      window.addEventListener = origAdd;
    }

    muteThemeHistory();
    if (document.querySelector('[data-cart-drawer]')) {
      var cartToggles = document.querySelectorAll('[data-cart-drawer-toggle]');
      for (var t = 0; t < cartToggles.length; t++) cartToggles[t].setAttribute('role', 'button');
    }
    if (localeChanged) {
      var drawer = document.querySelector('[data-section-type="cart-drawer"]');
      var incomingDrawer = doc.querySelector('[data-section-type="cart-drawer"]');
      if (drawer && incomingDrawer) drawer.innerHTML = incomingDrawer.innerHTML;
      requestAnimationFrame(function () {
        if (window.Spruce && typeof window.Spruce.updateSubscribers === 'function') {
          window.Spruce.updateSubscribers();
        }
      });
    }

    // 6. 遅延読み込みと in-view 演出を作動させる
    try { window.dispatchEvent(new CustomEvent('label:section:hasmutated', { detail: { url: url } })); } catch (e) {}
    try { if (window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.checkElems(); } catch (e) {}

    pageview();
    try { document.dispatchEvent(new CustomEvent('mf:softnav', { detail: { url: url } })); } catch (e) {}
    return true;
  }

  function syncPageHead(doc) {
    document.title = doc.title || document.title;
    var selector = 'link[rel="canonical"],link[rel="alternate"][hreflang],' +
      'meta[name="description"],meta[name="robots"],meta[property^="og:"],meta[name^="twitter:"]';
    var current = document.head.querySelectorAll(selector);
    var incoming = doc.head.querySelectorAll(selector);
    for (var i = 0; i < current.length; i++) current[i].parentNode.removeChild(current[i]);
    for (var j = 0; j < incoming.length; j++) document.head.appendChild(incoming[j].cloneNode(true));
  }

  function syncThemeContext(doc) {
    var incoming = doc.getElementById('mf-theme-config');
    if (!incoming) return;
    var config = JSON.parse(incoming.textContent);
    if (!config.strings || !config.routes) return;
    window.theme = window.theme || {};
    window.theme.strings = config.strings;
    window.theme.routes = config.routes;
    window.theme.moneyFormat = config.moneyFormat;
    var current = document.getElementById('mf-theme-config');
    if (current) current.textContent = incoming.textContent;
    if (window.Shopify && config.routes.root_url) {
      window.Shopify.routes = window.Shopify.routes || {};
      window.Shopify.routes.root = config.routes.root_url.replace(/\/?$/, '/');
    }
  }

  function pageview() {
    try { if (typeof window.gtag === 'function') window.gtag('event', 'page_view', { page_location: location.href, page_path: location.pathname, page_title: document.title }); } catch (e) {}
    try { if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.page) window.ShopifyAnalytics.lib.page(null, { path: location.pathname, title: document.title }); } catch (e) {}
    try { if (window.fbq) window.fbq('track', 'PageView'); } catch (e) {}
  }

  /* ---------------------------------------------------------------- 遷移 */

  function saveScroll(link) {
    renderedUrl = location.pathname + location.search + location.hash;
    var place = placeForLink(link);
    try { history.replaceState(stateFor(window.scrollY, null, place), '', location.href); } catch (e) {}
  }

  function clearNavigationError() {
    var panel = document.getElementById('mf-softnav-error');
    if (panel) panel.remove();
  }

  function navigationError(url, push, restoreY, restorePlaceState) {
    clearNavigationError();
    var main = document.querySelector(MAIN);
    if (!main) return;
    var en = /^\/en(?:\/|$)/.test(location.pathname);
    var panel = document.createElement('div');
    panel.id = 'mf-softnav-error';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.style.cssText = 'margin:16px;padding:16px;border:1px solid currentColor;font:inherit;line-height:1.7';
    var message = document.createElement('p');
    message.textContent = en ? 'This page could not be loaded. Your current page and player have been kept.' :
      'ページを読み込めませんでした。表示中のページと試聴プレイヤーはそのまま保持しています。';
    panel.appendChild(message);
    var retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = en ? 'Try again' : '再試行';
    retry.style.cssText = 'min-height:44px;margin-right:16px;text-decoration:underline;font:inherit';
    retry.addEventListener('click', function () {
      delete CACHE[routeUrl(url).split('#')[0]];
      go(url, push, restoreY, restorePlaceState);
    });
    panel.appendChild(retry);
    var close = document.createElement('button');
    close.type = 'button';
    close.textContent = en ? 'Close' : '閉じる';
    close.style.cssText = retry.style.cssText;
    close.addEventListener('click', clearNavigationError);
    panel.appendChild(close);
    var normal = document.createElement('a');
    normal.href = url;
    normal.setAttribute('data-no-softnav', '');
    normal.textContent = en ? 'Open normally (interrupts playback)' : '通常のページを開く（試聴は中断します）';
    normal.style.cssText = 'display:inline-flex;align-items:center;min-height:44px;text-decoration:underline';
    panel.appendChild(normal);
    main.insertBefore(panel, main.firstChild);
    /* A failed history navigation must not leave the URL describing unseen content. */
    if (!push) {
      try { history.replaceState(stateFor(window.scrollY, new URL(renderedUrl, location.href).search), '', renderedUrl); } catch (e) {}
    }
  }

  function go(url, push, restoreY, restorePlaceState, options) {
    url = routeUrl(url);
    var target = new URL(url, location.href);
    var protectPlayer = INFO.test(target.pathname) || INFO.test(new URL(renderedUrl, location.href).pathname);
    var attempt = ++navigationId;
    clearNavigationError();
    barStart();
    fetchPage(url).then(function (html) {
      if (attempt !== navigationId) return;
      if (needsFullProductLoad(html, url)) {
        location.href = routeUrl(url);
        return;
      }
      if (!swap(html, url)) throw new Error('missing main content');
      var y = options && options.preserveScroll ? restoreY : (push ? 0 : restoreY);
      if (push) history.pushState(stateFor(y, target.search), '', url);
      renderedUrl = url;
      scrollDestination(url, y, push ? null : restorePlaceState);
      if (options && options.focusId) {
        var focus = document.getElementById(options.focusId);
        if (focus) focus.focus({ preventScroll: true });
      }
      if (options && options.announceResults) {
        var count = document.getElementById('facets-results-count');
        var announce = document.getElementById('screenreader-announce');
        if (count && announce) announce.textContent = count.textContent;
      }
      barDone();
    }).catch(function (err) {
      if (attempt !== navigationId || (err && err.name === 'AbortError')) return;
      barDone();
      if (protectPlayer) { navigationError(url, push, restoreY, restorePlaceState); return; }
      location.href = url;    // それ以外のページは従来の通常遷移フォールバックを維持
    });
  }

  /* ---------------------------------------------------------------- 言語切り替え
     Shopify 標準の localization form は通常の POST → full document reload になるため、
     #mf-player / #mf-yt も作り直され、音が一瞬途切れる。
     言語だけは POST を fetch で行い、返ってきた翻訳済みHTMLを既存の swap() で差し替える。
     body 直下のプレイヤーは保持されるので再生を継続できる。
     国/通貨切替は価格・市場状態への影響が大きいため、従来どおり通常遷移に任せる。 */
  var localeBusy = false;
  function localeSwitch(option, e) {
    if (!option || localeBusy) return false;
    var box = option.closest && option.closest('[data-disclosure-locale]');
    if (!box) return false;
    var form = option.closest('form[data-disclosure-form]');
    var input = box.querySelector('[name="locale_code"]');
    var value = option.getAttribute('data-value');
    if (!form || !input || !value || value === input.value) return false;

    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    }

    localeBusy = true;
    var y = window.scrollY || 0;
    var previous = input.value;
    input.value = value;
    barStart();

    var fd = new FormData(form);
    fetch(form.action || '/localization', {
      method: (form.method || 'post').toUpperCase(),
      body: fd,
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { 'X-Requested-With': 'mf-localization' }
    }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      var target = routeUrl(r.url || location.href);
      return r.text().then(function (html) { return { html: html, target: target }; });
    }).then(function (out) {
      if (!swap(out.html, out.target)) throw new Error('missing main content');
      var target = new URL(out.target, location.href);
      history.pushState(stateFor(y, target.search), '', out.target);
      renderedUrl = out.target;
      window.scrollTo(0, y);
      barDone();
      localeBusy = false;
    }).catch(function () {
      barDone();
      localeBusy = false;
      input.value = previous;
      // 最後の保険。切替自体を失敗させるより、標準の言語切替を実行する。
      input.value = value;
      try { HTMLFormElement.prototype.submit.call(form); } catch (err) { form.submit(); }
    });
    return true;
  }

  /* プレイヤーのジャケット・商品名が「いま開いている商品ページ」を指すときは、
     再読み込み（＝再生が止まる）にせず、ページ先頭へ戻るだけにする（2026-10-20） */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('#mf-player a.mf-thumb-link[href], #mf-player .mf-info__product a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.pathname === location.pathname && a.search === location.search) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, true);

  document.addEventListener('click', function (e) {
    var option = e.target.closest && e.target.closest('[data-disclosure-locale] [data-disclosure-option]');
    if (option) localeSwitch(option, e);
  }, true);

  /* Policy/help links get early interception so the persistent player is never
     torn down by a full document navigation. Existing player controls, forms,
     new-tab gestures and explicit opt-outs keep their original behavior. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || !INFO.test(a.pathname) || !navigable(a, e)) return;
    e.preventDefault();
    e.stopPropagation();
    saveScroll(a);
    go(a.pathname + a.search + a.hash, true);
  }, true);

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('.mf-collection-tools__stock a[href]');
    if (!navigable(a, e)) return;
    var target = new URL(location.href);
    if (a.getAttribute('href').indexOf('filter.v.availability=1') !== -1) {
      target.searchParams.set('filter.v.availability', '1');
    } else {
      target.searchParams.delete('filter.v.availability');
    }
    target.searchParams.delete('page');
    e.preventDefault();
    e.stopPropagation();
    saveScroll(a);
    go(target.pathname + target.search + target.hash, true);
  }, true);

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!navigable(a, e)) return;
    e.preventDefault();
    saveScroll(a);
    go(a.pathname + a.search + a.hash, true);
  });

  // hover / タッチ開始で先読み
  var hoverTimer = null;
  document.addEventListener('mouseover', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!navigable(a)) return;
    clearTimeout(hoverTimer);
    var u = a.pathname + a.search;
    hoverTimer = setTimeout(function () { prefetch(u); }, 90);
  });
  document.addEventListener('touchstart', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (navigable(a)) prefetch(a.pathname + a.search);
  }, { passive: true });

  // キャプチャ段階で登録する。ブラウザによっては先に登録済みのテーマのリスナーより前に動けるが、
  // Chromium では登録順のまま（テーマが先）だったため、主対策は muteThemeHistory と swap 内のリスナー破棄。
  // ここの stopImmediatePropagation は、後から登録されたリスナーを止めるための保険
  window.addEventListener('popstate', function (e) {
    var shown = new URL(renderedUrl, location.href);
    if (INFO.test(location.pathname) && shown.pathname === location.pathname && shown.search === location.search) {
      // Native #anchors can have null state; do not turn Back/Forward into reload.
      e.stopImmediatePropagation();
      ++navigationId;
      if (busy) { try { busy.abort(); } catch (error) {} }
      clearNavigationError(); barDone();
      renderedUrl = location.pathname + location.search + location.hash;
      scrollDestination(renderedUrl, e.state && e.state.y, e.state && e.state.place);
      return;
    }
    if (!e.state && INFO.test(location.pathname)) {
      e.stopImmediatePropagation();
      go(location.pathname + location.search + location.hash, false, 0);
      return;
    }
    if (!e.state) { location.reload(); return; }          // 誰の状態でもない履歴は通常の再読み込み
    if (!e.state.mf) return;                               // テーマ自身の状態（フィルタ・並べ替え）はテーマに任せる
    e.stopImmediatePropagation();                          // テーマの filterable による再描画を止める
    go(location.pathname + location.search + location.hash, false, e.state.y || 0, e.state.place || null);
  }, true);

  /* Full document fallback（主に sold-out + ReStock）から戻った場合でも、
     source entry に残った mf state を消さず、同じカード位置へ戻す。 */
  window.addEventListener('pageshow', function () {
    var st = history.state;
    if (!st || !st.mf || !st.place) return;
    setTimeout(function () { restorePlace(st.place, st.y || 0); }, 0);
  });

  // 最初のページにも状態を持たせる。既存の mf state がある場合は
  // hard navigation から戻った履歴なので、scroll/place を上書きしない。
  try {
    var initialState = history.state;
    history.replaceState(initialState && initialState.mf ? initialState : stateFor(window.scrollY || 0), '', location.href);
  } catch (e) {}

  // 初回の filterable を黙らせる。Alpine の初期化がこのスクリプトより後になる場合に備えて数回試す
  muteThemeHistory();
  document.addEventListener('DOMContentLoaded', muteThemeHistory);
  window.addEventListener('load', muteThemeHistory);
  setTimeout(muteThemeHistory, 1500);
})();
